import { parseCalculatorUserOverlayDocumentV4 } from "../calculator-user-layer-v4-contract.js";
import {
    createCalculatorLootProjectionValidator,
} from "./calculator-loot-projection-contract.js";
import {
    createCalculatorStatTermSubjectParser,
} from "./calculator-stat-term-subject-contract.js";
import {
    calculatorFishingSourcesEqual,
    canonicalizeCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";
import { validateCalculatorFactKeyShapeV1 } from "./calculator-fact-key-contract.js";
import {
    validateCalculatorModelSelectionV1,
    validateCalculatorScenarioV1,
} from "./calculator-scenario-contract.js";
import {
    parseCalculatorSourceMainGroupPresentation,
} from "./calculator-presentation-contract.js";

const MODEL_FIELDS = Object.freeze([
    "schemaVersion",
    "engineVersion",
    "resolvedRevision",
    "modelRevision",
    "modelHash",
    "selection",
    "baseFacts",
    "sourceLayers",
    "catalog",
    "defaultScenario",
]);
const PROJECTION_FIELDS = Object.freeze([
    "schemaVersion",
    "engineVersion",
    "modelRevision",
    "userLayerContentHash",
    "normalizedScenario",
    "resourceAbundance",
    "summaries",
    "timeline",
    "statBreakdowns",
    "target",
    "rateAlternativeResolutions",
    "sankey",
    "groupRateAuthoring",
    "unquantifiedItems",
    "provenanceTraces",
    "presentationOrder",
]);
const LAYER_FIELDS = Object.freeze([
    "layerId",
    "name",
    "order",
    "evidenceClass",
    "provenance",
]);
const TRACE_FIELDS = Object.freeze([
    "traceId",
    "factKey",
    "effectiveFactId",
    "candidates",
]);
const CANDIDATE_FIELDS = Object.freeze([
    "operationId",
    "status",
    "deactivatedByOperationIds",
]);
const RESOLVED_CANDIDATE_FIELDS = Object.freeze([
    "deactivators",
    "layer",
    "operation",
    "status",
    "deactivatedByOperationIds",
]);
const TRACE_LAYER_FIELDS = Object.freeze([
    "layerId",
    "name",
    "order",
    "kind",
    "evidenceClass",
]);
const DATA_PRESENTATION_FIELDS = Object.freeze(["modelRevision", "sankey"]);
const SANKEY_PRESENTATION_FIELDS = Object.freeze([
    "conditionsByOperationId",
    "groupsByGroupId",
    "itemsByItemId",
    "labels",
    "locale",
    "numberFormats",
    "provenance",
    "unknownItem",
]);
const SANKEY_GROUP_FIELDS = Object.freeze([
    "connectorColor",
    "fillColor",
    "label",
    "sourceMainGroup",
    "strokeColor",
    "textColor",
]);
const SANKEY_ITEM_FIELDS = Object.freeze([
    "connectorColor",
    "fillColor",
    "gradeRank",
    "iconGradeTone",
    "iconUrl",
    "label",
    "strokeColor",
    "textColor",
]);
const TRACE_STATUSES = new Set([
    "effective",
    "overridden",
    "refuted",
    "inactiveCondition",
]);
const RESOURCE_ABUNDANCE_STATES = new Set([
    "exhausted",
    "low",
    "average",
    "abundant",
]);
const RATE_BASES = new Set([
    "ratio",
    "percent",
    "denormalizedWeight",
    "rawWeight",
    "perCatch",
    "perHour",
]);
const UNAVAILABLE_REASON_CODES = new Set([
    "missingSourceFact",
    "unsupportedSourceVariant",
    "noEquivalentRawWeight",
    "incompleteProvenance",
    "notApplicable",
    "modelSelectionRequired",
]);
const TRACE_LAYER_KINDS = new Set(["base", "source", "user"]);
const SOURCE_EVIDENCE_CLASSES = new Set([
    "sourceTextInference",
    "importedEnrichment",
    "communityClaim",
    "manualEnrichment",
]);
const SANKEY_ITEM_GRADE_TONES = new Set([
    "red",
    "yellow",
    "blue",
    "green",
    "white",
    "unknown",
]);
const ITEM_ICON_PATH = /^\/images\/items\/[A-Za-z0-9][A-Za-z0-9_-]*\.webp$/;
const FISHING_SOURCE_FACT_KINDS = new Set([
    "fishingSourceBiteTime",
    "groupMembership",
    "groupPresence",
    "itemPresence",
    "itemRate",
    "itemQuantity",
    "groupRate",
]);

function runtimeCdnOrigin() {
    const value = globalThis.__fishystuffRuntimeConfig?.cdnBaseUrl;
    if (typeof value !== "string" || !value) {
        return null;
    }
    try {
        const url = new URL(value);
        if (
            !["http:", "https:"].includes(url.protocol)
            || url.username
            || url.password
            || url.search
            || url.hash
        ) {
            return null;
        }
        return url.origin;
    } catch {
        return null;
    }
}

function isLoopbackHostname(value) {
    return value === "127.0.0.1" || value === "localhost";
}

function isRuntimeCdnOrigin(url) {
    const configuredOrigin = runtimeCdnOrigin();
    if (!configuredOrigin) {
        return false;
    }
    if (url.origin === configuredOrigin) {
        return true;
    }
    const configuredUrl = new URL(configuredOrigin);
    return isLoopbackHostname(url.hostname)
        && isLoopbackHostname(configuredUrl.hostname)
        && url.protocol === configuredUrl.protocol
        && url.port === configuredUrl.port;
}

export function isCalculatorPresentationIconUrl(value) {
    if (typeof value !== "string" || !value) {
        return false;
    }
    if (value.startsWith("/") && !value.startsWith("//")) {
        return ITEM_ICON_PATH.test(value);
    }
    let url;
    try {
        url = new URL(value);
    } catch {
        return false;
    }
    if (
        !["http:", "https:"].includes(url.protocol)
        || url.username
        || url.password
        || url.search
        || url.hash
        || !ITEM_ICON_PATH.test(url.pathname)
    ) {
        return false;
    }
    const sameOrigin = typeof globalThis.location?.origin === "string"
        && url.origin === globalThis.location.origin;
    return sameOrigin || isRuntimeCdnOrigin(url);
}
const HASH_PATTERN = /^(?:blake3|sha256):[0-9a-f]{64}$/;
const GENERATED_ID_PATTERN = /^blake3:[0-9a-f]{64}$/;
const U32_MAX = 4_294_967_295;

function isRecord(value) {
    return Boolean(value)
        && typeof value === "object"
        && !Array.isArray(value)
        && Object.prototype.toString.call(value) === "[object Object]";
}

function requireRecord(value, path) {
    if (!isRecord(value)) {
        throw new CalculatorLayerInspectorContractError(path, "expected an object");
    }
    return value;
}

function requireExactFields(value, fields, path) {
    const object = requireRecord(value, path);
    const allowed = new Set(fields);
    const unknown = Object.keys(object).filter((field) => !allowed.has(field));
    if (unknown.length) {
        throw new CalculatorLayerInspectorContractError(
            `${path}.${unknown[0]}`,
            "unknown field",
        );
    }
    for (const field of fields) {
        if (!Object.prototype.hasOwnProperty.call(object, field)) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.${field}`,
                "missing field",
            );
        }
    }
    return object;
}

function requireString(value, path) {
    if (typeof value !== "string" || !value.trim() || value !== value.trim()) {
        throw new CalculatorLayerInspectorContractError(
            path,
            "expected a non-empty string without surrounding whitespace",
        );
    }
    return value;
}

function requireInteger(value, path, minimum = 0, maximum = Number.MAX_SAFE_INTEGER) {
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
        throw new CalculatorLayerInspectorContractError(
            path,
            `expected an integer from ${minimum} through ${maximum}`,
        );
    }
    return value;
}

function requireArray(value, path) {
    if (!Array.isArray(value)) {
        throw new CalculatorLayerInspectorContractError(path, "expected an array");
    }
    return value;
}

function requireFiniteNonNegative(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        throw new CalculatorLayerInspectorContractError(
            path,
            "expected a finite non-negative number",
        );
    }
    return value;
}

function requireGeneratedId(value, path) {
    if (typeof value !== "string" || !GENERATED_ID_PATTERN.test(value)) {
        throw new CalculatorLayerInspectorContractError(
            path,
            "expected a canonical BLAKE3 identifier",
        );
    }
    return value;
}

function validateContentHash(value, path) {
    const object = requireExactFields(value, ["algorithm", "digest"], path);
    const combined = `${object.algorithm}:${object.digest}`;
    if (!HASH_PATTERN.test(combined)) {
        throw new CalculatorLayerInspectorContractError(path, "invalid content hash");
    }
}

function validateLayerProvenance(value, path) {
    const object = requireExactFields(
        value,
        ["sourceId", "sourceRevision", "contentHash", "importedAtUtc"],
        path,
    );
    requireString(object.sourceId, `${path}.sourceId`);
    if (object.sourceRevision !== null) {
        requireString(object.sourceRevision, `${path}.sourceRevision`);
    }
    validateContentHash(object.contentHash, `${path}.contentHash`);
    if (object.importedAtUtc !== null) {
        requireString(object.importedAtUtc, `${path}.importedAtUtc`);
    }
}

function validateLayer(value, path, operationsField, expectedKind) {
    const fields = [...LAYER_FIELDS, operationsField];
    const layer = requireExactFields(value, fields, path);
    requireString(layer.layerId, `${path}.layerId`);
    requireString(layer.name, `${path}.name`);
    requireInteger(layer.order, `${path}.order`, 0, U32_MAX);
    validateLayerProvenance(layer.provenance, `${path}.provenance`);
    requireArray(layer[operationsField], `${path}.${operationsField}`);
    if (expectedKind === "base" && layer.evidenceClass !== "structuredSourceFact") {
        throw new CalculatorLayerInspectorContractError(
            `${path}.evidenceClass`,
            "base facts must use structuredSourceFact",
        );
    }
    if (expectedKind === "source" && !SOURCE_EVIDENCE_CLASSES.has(layer.evidenceClass)) {
        throw new CalculatorLayerInspectorContractError(
            `${path}.evidenceClass`,
            "invalid source-layer evidence class",
        );
    }
}

function validateTraceLayer(value, path) {
    const layer = requireExactFields(value, TRACE_LAYER_FIELDS, path);
    requireString(layer.layerId, `${path}.layerId`);
    requireString(layer.name, `${path}.name`);
    requireInteger(layer.order, `${path}.order`, 0, U32_MAX);
    if (!TRACE_LAYER_KINDS.has(layer.kind)) {
        throw new CalculatorLayerInspectorContractError(`${path}.kind`, "invalid layer kind");
    }
    requireString(layer.evidenceClass, `${path}.evidenceClass`);
}

function validateCandidateState(object, path) {
    if (!TRACE_STATUSES.has(object.status)) {
        throw new CalculatorLayerInspectorContractError(
            `${path}.status`,
            "invalid trace candidate status",
        );
    }
    requireArray(
        object.deactivatedByOperationIds,
        `${path}.deactivatedByOperationIds`,
    ).forEach((operationId, operationIndex) => requireString(
        operationId,
        `${path}.deactivatedByOperationIds[${operationIndex}]`,
    ));
}

function validateResolvedDeactivator(value, path) {
    const object = requireExactFields(value, ["layer", "operation"], path);
    validateTraceLayer(object.layer, `${path}.layer`);
    const operation = requireRecord(object.operation, `${path}.operation`);
    if (operation.kind !== "refuteEvidence") {
        throw new CalculatorLayerInspectorContractError(
            `${path}.operation.kind`,
            "expected refuteEvidence",
        );
    }
    requireString(operation.operationId, `${path}.operation.operationId`);
}

function validateTrace(value, path, { resolved = false } = {}) {
    const trace = requireExactFields(value, TRACE_FIELDS, path);
    requireGeneratedId(trace.traceId, `${path}.traceId`);
    validateCalculatorFactKeyShapeV1(trace.factKey, `${path}.factKey`, {
        fail(issuePath, message) {
            throw new CalculatorLayerInspectorContractError(issuePath, message);
        },
        requireExactFields,
        requireRecord,
        requireString,
    });
    if (FISHING_SOURCE_FACT_KINDS.has(trace.factKey.kind)) {
        validateFishingSource(
            trace.factKey.fishingSource,
            `${path}.factKey.fishingSource`,
        );
    }
    if (trace.effectiveFactId !== null) {
        requireGeneratedId(trace.effectiveFactId, `${path}.effectiveFactId`);
    }
    requireArray(trace.candidates, `${path}.candidates`).forEach((candidate, index) => {
        const candidatePath = `${path}.candidates[${index}]`;
        const fields = resolved ? RESOLVED_CANDIDATE_FIELDS : CANDIDATE_FIELDS;
        const object = requireExactFields(candidate, fields, candidatePath);
        if (resolved) {
            validateTraceLayer(object.layer, `${candidatePath}.layer`);
            const operation = requireRecord(object.operation, `${candidatePath}.operation`);
            requireString(operation.kind, `${candidatePath}.operation.kind`);
            requireString(operation.operationId, `${candidatePath}.operation.operationId`);
            requireArray(object.deactivators, `${candidatePath}.deactivators`)
                .forEach((deactivator, deactivatorIndex) => validateResolvedDeactivator(
                    deactivator,
                    `${candidatePath}.deactivators[${deactivatorIndex}]`,
                ));
        } else {
            requireString(object.operationId, `${candidatePath}.operationId`);
        }
        validateCandidateState(object, candidatePath);
    });
}

function validateUnavailableReason(value, path) {
    const reason = requireExactFields(
        value,
        ["code", "sourceId", "detailCode"],
        path,
    );
    if (!UNAVAILABLE_REASON_CODES.has(reason.code)) {
        throw new CalculatorLayerInspectorContractError(
            `${path}.code`,
            "unsupported unavailable reason code",
        );
    }
    if (reason.sourceId !== null) {
        requireString(reason.sourceId, `${path}.sourceId`);
    }
    if (reason.detailCode !== null) {
        requireString(reason.detailCode, `${path}.detailCode`);
    }
}

function validateRate(value, path) {
    const rate = requireRecord(value, path);
    if (rate.state === "known") {
        requireExactFields(rate, ["state", "value", "basis"], path);
        requireFiniteNonNegative(rate.value, `${path}.value`);
        if (!RATE_BASES.has(rate.basis)) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.basis`,
                "unsupported rate basis",
            );
        }
        return rate;
    }
    if (rate.state === "unknown") {
        requireExactFields(rate, ["state"], path);
        return rate;
    }
    if (rate.state === "unavailable") {
        requireExactFields(rate, ["state", "reason"], path);
        validateUnavailableReason(rate.reason, `${path}.reason`);
        return rate;
    }
    throw new CalculatorLayerInspectorContractError(
        `${path}.state`,
        "unsupported rate state",
    );
}

function validateItemQuantity(value, path) {
    const quantity = requireRecord(value, path);
    if (quantity.state === "known") {
        requireExactFields(quantity, ["state", "minimum", "maximum"], path);
        const minimum = requireInteger(quantity.minimum, `${path}.minimum`, 0, U32_MAX);
        const maximum = requireInteger(quantity.maximum, `${path}.maximum`, 0, U32_MAX);
        if (maximum < minimum) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.maximum`,
                "must be at least the minimum",
            );
        }
        return quantity;
    }
    if (quantity.state === "unknown") {
        return requireExactFields(quantity, ["state"], path);
    }
    if (quantity.state === "unavailable") {
        requireExactFields(quantity, ["state", "reason"], path);
        validateUnavailableReason(quantity.reason, `${path}.reason`);
        return quantity;
    }
    throw new CalculatorLayerInspectorContractError(
        `${path}.state`,
        "unsupported item quantity state",
    );
}

function validateExpectedItemQuantity(value, path) {
    const quantity = requireRecord(value, path);
    if (quantity.state === "known") {
        requireExactFields(quantity, ["state", "value", "unit"], path);
        requireFiniteNonNegative(quantity.value, `${path}.value`);
        if (quantity.unit !== "count") {
            throw new CalculatorLayerInspectorContractError(
                `${path}.unit`,
                "expected count",
            );
        }
        return quantity;
    }
    if (quantity.state === "unknown") {
        return requireExactFields(quantity, ["state"], path);
    }
    if (quantity.state === "unavailable") {
        requireExactFields(quantity, ["state", "reason"], path);
        validateUnavailableReason(quantity.reason, `${path}.reason`);
        return quantity;
    }
    throw new CalculatorLayerInspectorContractError(
        `${path}.state`,
        "unsupported expected item quantity state",
    );
}

export class CalculatorLayerInspectorContractError extends TypeError {
    constructor(path, message) {
        super(`Calculator layer inspector contract failed at ${path}: ${message}`);
        this.name = "CalculatorLayerInspectorContractError";
        this.path = path;
    }
}

function validateFishingSource(value, path) {
    return canonicalizeCalculatorFishingSourceV1(
        value,
        path,
        (issuePath, _code, message) => {
            throw new CalculatorLayerInspectorContractError(issuePath, message);
        },
    );
}

const parseStatTermSubject = createCalculatorStatTermSubjectParser({
    fail(path, message) {
        throw new CalculatorLayerInspectorContractError(path, message);
    },
    requireBoolean(value, path) {
        if (typeof value !== "boolean") {
            throw new CalculatorLayerInspectorContractError(path, "expected a boolean");
        }
        return value;
    },
    requireExactFields,
    requireFinite(value, path, { minimum = -Infinity, maximum = Infinity } = {}) {
        if (!Number.isFinite(value) || value < minimum || value > maximum) {
            throw new CalculatorLayerInspectorContractError(
                path,
                "expected a finite number in range",
            );
        }
        return value;
    },
    requireInteger(value, path, { minimum = 0, maximum = Number.MAX_SAFE_INTEGER } = {}) {
        return requireInteger(value, path, minimum, maximum);
    },
    requireRecord,
    requireString,
});

const validateLootProjection = createCalculatorLootProjectionValidator({
    fail(path, message) {
        throw new CalculatorLayerInspectorContractError(path, message);
    },
    requireArray,
    requireExactFields,
    requireItemId(value, path) {
        return requireInteger(value, path, 1, U32_MAX);
    },
    requireRecord,
    requireString,
    validateExpectedItemQuantity,
    validateItemQuantity,
    validateRate,
    validateStatTermSubject(value, path) {
        return parseStatTermSubject(value, path);
    },
    validateTraceId(value, path) {
        return requireGeneratedId(value, path);
    },
});

export function parseCalculatorInspectorModelV1(value) {
    const model = requireExactFields(value, MODEL_FIELDS, "$.model");
    if (model.schemaVersion !== 1) {
        throw new CalculatorLayerInspectorContractError(
            "$.model.schemaVersion",
            "expected exactly 1",
        );
    }
    requireString(model.engineVersion, "$.model.engineVersion");
    requireGeneratedId(model.modelRevision, "$.model.modelRevision");
    requireGeneratedId(model.modelHash, "$.model.modelHash");
    requireString(model.resolvedRevision, "$.model.resolvedRevision");
    validateCalculatorModelSelectionV1(
        model.selection,
        "$.model.selection",
        {
            requireExactFields,
            fail(path, message) {
                throw new CalculatorLayerInspectorContractError(path, message);
            },
        },
    );
    validateCalculatorScenarioV1(
        model.defaultScenario,
        "$.model.defaultScenario",
        {
            requireExactFields,
            fail(path, message) {
                throw new CalculatorLayerInspectorContractError(path, message);
            },
        },
    );
    validateLayer(model.baseFacts, "$.model.baseFacts", "facts", "base");
    requireArray(model.sourceLayers, "$.model.sourceLayers").forEach((layer, index) => {
        validateLayer(layer, `$.model.sourceLayers[${index}]`, "operations", "source");
    });
    const catalog = requireExactFields(model.catalog, ["entries"], "$.model.catalog");
    requireArray(catalog.entries, "$.model.catalog.entries").forEach((entry, index) => {
        const path = `$.model.catalog.entries[${index}]`;
        const object = requireExactFields(entry, ["optionId", "kind", "itemId"], path);
        requireString(object.optionId, `${path}.optionId`);
        requireString(object.kind, `${path}.kind`);
        if (object.itemId !== null) {
            requireInteger(object.itemId, `${path}.itemId`, 1, U32_MAX);
        }
    });
    return model;
}

function parseProjection(value, model, { resolved }) {
    const projection = requireExactFields(value, PROJECTION_FIELDS, "$.projection");
    if (projection.schemaVersion !== 1) {
        throw new CalculatorLayerInspectorContractError(
            "$.projection.schemaVersion",
            "expected exactly 1",
        );
    }
    requireString(projection.engineVersion, "$.projection.engineVersion");
    requireGeneratedId(projection.modelRevision, "$.projection.modelRevision");
    if (projection.userLayerContentHash !== null) {
        validateContentHash(
            projection.userLayerContentHash,
            "$.projection.userLayerContentHash",
        );
    }
    if (model) {
        if (projection.engineVersion !== model.engineVersion) {
            throw new CalculatorLayerInspectorContractError(
                "$.projection.engineVersion",
                "does not match the active model",
            );
        }
        if (projection.modelRevision !== model.modelRevision) {
            throw new CalculatorLayerInspectorContractError(
                "$.projection.modelRevision",
                "does not match the active model",
            );
        }
    }
    validateCalculatorScenarioV1(
        projection.normalizedScenario,
        "$.projection.normalizedScenario",
        {
            requireExactFields,
            fail(path, message) {
                throw new CalculatorLayerInspectorContractError(path, message);
            },
        },
    );
    if (!RESOURCE_ABUNDANCE_STATES.has(projection.resourceAbundance)) {
        throw new CalculatorLayerInspectorContractError(
            "$.projection.resourceAbundance",
            "unsupported resource abundance state",
        );
    }
    requireRecord(projection.summaries, "$.projection.summaries");
    requireRecord(projection.timeline, "$.projection.timeline");
    requireArray(projection.statBreakdowns, "$.projection.statBreakdowns");
    requireRecord(projection.target, "$.projection.target");
    const modelAlternativeFamilies = new Map();
    if (model) {
        const operations = [
            ...(model.baseFacts?.facts ?? []),
            ...(model.sourceLayers ?? []).flatMap((layer) => layer.operations ?? []),
        ];
        for (const operation of operations) {
            const alternative = operation?.alternative;
            if (!alternative) {
                continue;
            }
            const alternatives = modelAlternativeFamilies.get(alternative.familyId)
                ?? new Set();
            alternatives.add(alternative.alternativeId);
            modelAlternativeFamilies.set(alternative.familyId, alternatives);
        }
    }
    const resolutionFamilies = new Set();
    requireArray(
        projection.rateAlternativeResolutions,
        "$.projection.rateAlternativeResolutions",
    ).forEach((entry, index) => {
        const path = `$.projection.rateAlternativeResolutions[${index}]`;
        const resolution = requireRecord(entry, path);
        let parsed;
        if (resolution.kind === "automatic" || resolution.kind === "override") {
            parsed = requireExactFields(
                resolution,
                ["kind", "familyId", "alternativeId"],
                path,
            );
            requireString(parsed.alternativeId, `${path}.alternativeId`);
        } else if (
            resolution.kind === "selectionRequired"
            || resolution.kind === "inactive"
        ) {
            parsed = requireExactFields(resolution, ["kind", "familyId"], path);
        } else {
            throw new CalculatorLayerInspectorContractError(
                `${path}.kind`,
                "expected automatic, override, selectionRequired, or inactive",
            );
        }
        const familyId = requireString(parsed.familyId, `${path}.familyId`);
        if (resolutionFamilies.has(familyId)) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.familyId`,
                "expected exactly one resolution per family",
            );
        }
        if (model && !modelAlternativeFamilies.has(familyId)) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.familyId`,
                "does not identify a model rate-alternative family",
            );
        }
        if (
            model
            && parsed.alternativeId !== undefined
            && !modelAlternativeFamilies.get(familyId)?.has(parsed.alternativeId)
        ) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.alternativeId`,
                "does not identify an alternative in this family",
            );
        }
        resolutionFamilies.add(familyId);
    });
    if (model) {
        for (const familyId of modelAlternativeFamilies.keys()) {
            if (!resolutionFamilies.has(familyId)) {
                throw new CalculatorLayerInspectorContractError(
                    "$.projection.rateAlternativeResolutions",
                    `is missing model family ${familyId}`,
                );
            }
        }
    }
    validateLootProjection(
        projection.sankey,
        projection.unquantifiedItems,
        projection.groupRateAuthoring,
        projection.provenanceTraces,
        "$.projection",
    );
    requireRecord(projection.presentationOrder, "$.projection.presentationOrder");
    requireArray(projection.provenanceTraces, "$.projection.provenanceTraces")
        .forEach((trace, index) => validateTrace(
            trace,
            `$.projection.provenanceTraces[${index}]`,
            { resolved },
        ));
    return projection;
}

/** Parses the compact API/Wasm wire projection before operation resolution. */
export function parseCalculatorInspectorProjectionV1(value, model = null) {
    return parseProjection(value, model, { resolved: false });
}

/** Parses the props-down projection after operation references are hydrated. */
export function parseCalculatorInspectorResolvedProjectionV1(value, model = null) {
    return parseProjection(value, model, { resolved: true });
}

export function parseCalculatorInspectorUserLayerDocumentV4(value) {
    return parseCalculatorUserOverlayDocumentV4(value);
}

export function parseCalculatorInspectorCustomCatalogItems(value) {
    const items = requireArray(value, "$.customCatalogItems").map((entry, index) => {
        const path = `$.customCatalogItems[${index}]`;
        const object = requireExactFields(entry, ["itemId", "label"], path);
        const itemId = requireInteger(object.itemId, `${path}.itemId`, 1, U32_MAX);
        const label = requireString(object.label, `${path}.label`).trim();
        if (!label) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.label`,
                "must not be empty",
            );
        }
        return Object.freeze({ itemId, label });
    });
    const duplicateIndex = items.findIndex((item, index) => (
        items.findIndex((candidate) => candidate.itemId === item.itemId) !== index
    ));
    if (duplicateIndex >= 0) {
        throw new CalculatorLayerInspectorContractError(
            `$.customCatalogItems[${duplicateIndex}].itemId`,
            "duplicates an earlier custom item",
        );
    }
    return items;
}

/**
 * Parses the narrow, revision-bound presentation envelope consumed by the
 * Data inspector. The inspector reads labels only, but validates their exact
 * Sankey records so presentation data cannot become a loose fallback shape.
 */
export function parseCalculatorInspectorDataPresentationV1(value, model = null) {
    if (value === null) {
        return null;
    }
    const envelope = requireExactFields(
        value,
        DATA_PRESENTATION_FIELDS,
        "$.dataPresentation",
    );
    requireGeneratedId(envelope.modelRevision, "$.dataPresentation.modelRevision");
    if (model && envelope.modelRevision !== model.modelRevision) {
        throw new CalculatorLayerInspectorContractError(
            "$.dataPresentation.modelRevision",
            "does not match the active model",
        );
    }
    const sankey = requireExactFields(
        envelope.sankey,
        SANKEY_PRESENTATION_FIELDS,
        "$.dataPresentation.sankey",
    );
    const groups = requireRecord(
        sankey.groupsByGroupId,
        "$.dataPresentation.sankey.groupsByGroupId",
    );
    const conditions = requireRecord(
        sankey.conditionsByOperationId,
        "$.dataPresentation.sankey.conditionsByOperationId",
    );
    for (const [operationId, text] of Object.entries(conditions)) {
        requireString(operationId, "$.dataPresentation.sankey.conditionsByOperationId key");
        requireString(
            text,
            `$.dataPresentation.sankey.conditionsByOperationId.${operationId}`,
        );
    }
    for (const [groupId, value] of Object.entries(groups)) {
        requireString(groupId, "$.dataPresentation.sankey.groupsByGroupId key");
        const path = `$.dataPresentation.sankey.groupsByGroupId.${groupId}`;
        const group = requireExactFields(value, SANKEY_GROUP_FIELDS, path);
        requireString(group.label, `${path}.label`);
        requireString(group.connectorColor, `${path}.connectorColor`);
        requireString(group.fillColor, `${path}.fillColor`);
        parseCalculatorSourceMainGroupPresentation(
            group.sourceMainGroup,
            `${path}.sourceMainGroup`,
        );
        requireString(group.strokeColor, `${path}.strokeColor`);
        requireString(group.textColor, `${path}.textColor`);
    }
    const items = requireRecord(
        sankey.itemsByItemId,
        "$.dataPresentation.sankey.itemsByItemId",
    );
    for (const [itemId, value] of Object.entries(items)) {
        requireInteger(Number(itemId), `$.dataPresentation.sankey.itemsByItemId.${itemId}`, 1, U32_MAX);
        if (String(Number(itemId)) !== itemId) {
            throw new CalculatorLayerInspectorContractError(
                `$.dataPresentation.sankey.itemsByItemId.${itemId}`,
                "item key must be a canonical positive integer",
            );
        }
        const path = `$.dataPresentation.sankey.itemsByItemId.${itemId}`;
        const item = requireExactFields(value, SANKEY_ITEM_FIELDS, path);
        requireString(item.label, `${path}.label`);
        requireString(item.connectorColor, `${path}.connectorColor`);
        requireString(item.fillColor, `${path}.fillColor`);
        requireInteger(item.gradeRank, `${path}.gradeRank`, 0, U32_MAX);
        requireString(item.iconGradeTone, `${path}.iconGradeTone`);
        if (!SANKEY_ITEM_GRADE_TONES.has(item.iconGradeTone)) {
            throw new CalculatorLayerInspectorContractError(
                `${path}.iconGradeTone`,
                "unsupported presentation grade tone",
            );
        }
        if (item.iconUrl !== null && typeof item.iconUrl !== "string") {
            throw new CalculatorLayerInspectorContractError(
                `${path}.iconUrl`,
                "expected a string or null",
            );
        }
        if (item.iconUrl !== null) {
            requireString(item.iconUrl, `${path}.iconUrl`);
            if (!isCalculatorPresentationIconUrl(item.iconUrl)) {
                throw new CalculatorLayerInspectorContractError(
                    `${path}.iconUrl`,
                    "expected a same-origin or runtime-configured calculator item asset URL",
                );
            }
        }
        requireString(item.strokeColor, `${path}.strokeColor`);
        requireString(item.textColor, `${path}.textColor`);
    }
    return envelope;
}

export function orderedCalculatorLayerStack(model, userLayerDocument) {
    if (!model) {
        return [];
    }
    const base = Object.freeze({
        ...model.baseFacts,
        kind: "base",
        readOnly: true,
    });
    const source = model.sourceLayers
        .map((layer, index) => ({
            layer,
            index,
            phase: layer.evidenceClass === "sourceTextInference" ? 0 : 1,
        }))
        .sort((left, right) => (
            left.phase - right.phase
            || left.layer.order - right.layer.order
            || left.index - right.index
        ))
        .map(({ layer }) => Object.freeze({
            ...layer,
            kind: "source",
            readOnly: true,
        }));
    const user = userLayerDocument
        ? [Object.freeze({
            ...userLayerDocument.userLayer,
            kind: "user",
            readOnly: false,
        })]
        : [];
    return Object.freeze([base, ...source, ...user]);
}

export function calculatorTraceMatchesFishingSource(trace, fishingSource) {
    const traceFishingSource = trace?.factKey?.fishingSource;
    return traceFishingSource === undefined
        || traceFishingSource === null
        || calculatorFishingSourcesEqual(traceFishingSource, fishingSource);
}

export function calculatorFishingSourceTraces(projection, fishingSource) {
    return Object.freeze((projection?.provenanceTraces ?? []).filter((trace) => (
        calculatorTraceMatchesFishingSource(trace, fishingSource)
    )));
}

export function selectedCalculatorTrace(projection, traceId, fishingSource = null) {
    const traces = fishingSource === null
        ? projection?.provenanceTraces ?? []
        : calculatorFishingSourceTraces(projection, fishingSource);
    if (!traces.length) {
        return null;
    }
    return traces.find((trace) => trace.traceId === traceId) ?? traces[0];
}

export function effectiveCalculatorTraceLayer(trace) {
    return trace?.candidates?.find((candidate) => candidate.status === "effective")?.layer ?? null;
}

/**
 * Compares the strict v4 layer header plus the canonical operation commitment.
 *
 * The content hash commits the operations, but intentionally does not own
 * editable layer labels or loose layer provenance. Those fields are therefore
 * compared explicitly before a presentation owner skips a document update.
 */
export function calculatorInspectorUserLayerDocumentsEqual(left, right) {
    if (left === right) {
        return true;
    }
    const leftLayer = left?.userLayer;
    const rightLayer = right?.userLayer;
    const leftProvenance = leftLayer?.provenance;
    const rightProvenance = rightLayer?.provenance;
    const leftHash = leftProvenance?.contentHash;
    const rightHash = rightProvenance?.contentHash;
    return left?.documentVersion === right?.documentVersion
        && leftLayer?.layerId === rightLayer?.layerId
        && leftLayer?.name === rightLayer?.name
        && leftLayer?.order === rightLayer?.order
        && leftLayer?.evidenceClass === rightLayer?.evidenceClass
        && leftProvenance?.sourceId === rightProvenance?.sourceId
        && leftProvenance?.sourceRevision === rightProvenance?.sourceRevision
        && leftProvenance?.importedAtUtc === rightProvenance?.importedAtUtc
        && leftHash?.algorithm === rightHash?.algorithm
        && leftHash?.digest === rightHash?.digest;
}

export function availableCalculatorGroupIds(model, projection) {
    const ordered = [];
    const seen = new Set();
    const add = (groupId) => {
        if (typeof groupId === "string" && groupId && !seen.has(groupId)) {
            seen.add(groupId);
            ordered.push(groupId);
        }
    };
    for (const ranked of projection?.presentationOrder?.groups ?? []) {
        add(ranked.groupId);
    }
    const fishingSource = model?.selection?.fishingSource;
    for (const trace of calculatorFishingSourceTraces(projection, fishingSource)) {
        add(trace.factKey?.groupId);
    }
    for (const operation of model?.baseFacts?.facts ?? []) {
        if (
            !operation.fishingSource
            || calculatorFishingSourcesEqual(operation.fishingSource, fishingSource)
        ) {
            add(operation.groupId);
        }
    }
    for (const layer of model?.sourceLayers ?? []) {
        for (const operation of layer.operations) {
            if (
                !operation.fishingSource
                || calculatorFishingSourcesEqual(operation.fishingSource, fishingSource)
            ) {
                add(operation.groupId);
            }
        }
    }
    return Object.freeze(ordered);
}

export function calculatorItemLabel(itemId, customCatalogItems = []) {
    const match = customCatalogItems.find((entry) => entry.itemId === itemId);
    return match?.label ?? `Item ${itemId}`;
}
